import AiVideoPlayerModal from "./AiVideoPlayerModal";
import AiAudioPlayerModal from "./AiAudioPlayerModal";
import AiTextEditor from "../../Pages/AegisAI/Documents/AiTextEditor";
import React from 'react';
import { MdVisibility, MdDownload } from 'react-icons/md';
import ReAuthModal from './ReAuthModal';
import { previewProviderFile, downloadProviderFile } from '../../Services/ProviderService';
import { login as loginApi } from '../../Services/AuthenticationService';
import { useAuth } from '../../Context/AuthContext';
import { toast } from 'react-toastify';
import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@5.4.296/build/pdf.worker.min.mjs";

const PREVIEW_EXTENSIONS = {
  pdf: ['pdf'],
  docx: ['docx'],
  image: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'],
  audio: ['mp3', 'wav', 'm4a', 'aac', 'ogg', 'oga', 'flac'],
  video: ['mp4', 'webm', 'mov', 'm4v', 'ogv'],
  text: ['txt', 'md', 'csv', 'json', 'log'],
};

// Servers often send application/octet-stream, so the file extension is the fallback signal.
const getPreviewKind = (contentType, filename) => {
  const type = String(contentType || '').toLowerCase();
  const ext = (String(filename || '').match(/\.([a-z0-9]+)$/i)?.[1] || '').toLowerCase();
  if (type.includes('pdf') || PREVIEW_EXTENSIONS.pdf.includes(ext)) return 'pdf';
  if (type.includes('wordprocessingml') || PREVIEW_EXTENSIONS.docx.includes(ext)) return 'docx';
  if (ext === 'doc' || type === 'application/msword') return 'legacy-doc';
  if (type.startsWith('image/') || PREVIEW_EXTENSIONS.image.includes(ext)) return 'image';
  if (type.startsWith('audio/') || PREVIEW_EXTENSIONS.audio.includes(ext)) return 'audio';
  if (type.startsWith('video/') || PREVIEW_EXTENSIONS.video.includes(ext)) return 'video';
  if (type.startsWith('text/') || PREVIEW_EXTENSIONS.text.includes(ext)) return 'text';
  // Legacy callers relied on PDF being the default when nothing identifies the file.
  if ((!type || type === 'application/octet-stream') && !ext) return 'pdf';
  return 'unsupported';
};

function DocxPreview({ url }) {
  const containerRef = React.useRef(null);
  const [status, setStatus] = React.useState('loading');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setStatus('loading');
        const [{ renderAsync }, blob] = await Promise.all([
          import('docx-preview'),
          fetch(url).then((r) => r.blob()),
        ]);
        if (cancelled || !containerRef.current) return;
        containerRef.current.innerHTML = '';
        await renderAsync(blob, containerRef.current, undefined, {
          inWrapper: true,
          ignoreLastRenderedPageBreak: true,
          breakPages: true,
        });
        if (!cancelled) setStatus('ready');
      } catch (err) {
        console.error('DOCX render error', err);
        if (!cancelled) setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  return (
    <div className="overflow-auto h-100 position-relative hc-docx-preview" style={{ background: 'var(--hc-skeleton-bg)', borderRadius: 8 }}>
      {status === 'loading' && <div className="d-flex align-items-center justify-content-center h-100 position-absolute w-100">Rendering document...</div>}
      {status === 'error' && <div className="d-flex align-items-center justify-content-center h-100 position-absolute w-100 text-center px-3">Unable to render this Word document. Please download it to view.</div>}
      <div ref={containerRef} />
    </div>
  );
}

function TextPreview({ url, filename }) {
  const [text, setText] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    fetch(url).then((r) => r.text()).then((t) => { if (!cancelled) setText(t); }).catch(() => { if (!cancelled) setText(''); });
    return () => { cancelled = true; };
  }, [url]);
  if (text === null) return <div className="d-flex align-items-center justify-content-center h-100">Loading text content...</div>;
  return (
    <div className="h-100 p-2 overflow-hidden d-flex flex-column">
      <AiTextEditor value={text} readOnly={true} fileName={filename || 'document.txt'} minHeight="100%" maxHeight="100%" className="h-100 flex-grow-1" />
    </div>
  );
}

export default function FileActions({ fileId, fileName, className = '', showPreview = true, showDownload = true, compact = false, stack = false, onDownload, onPreview, skipReAuth = false }) {
  const { token, decodedToken } = useAuth();

  const [showReAuth, setShowReAuth] = React.useState(false);
  const [reauthPassword, setReauthPassword] = React.useState('');
  const [reauthLoading, setReauthLoading] = React.useState(false);
  const [reauthError, setReauthError] = React.useState('');
  const [showReauthPassword, setShowReauthPassword] = React.useState(false);
  const [action, setAction] = React.useState(null); // 'preview' | 'download'
  const [authToken, setAuthToken] = React.useState(null);

  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const [previewFilename, setPreviewFilename] = React.useState(null);
  const [previewContentType, setPreviewContentType] = React.useState(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [previewPage, setPreviewPage] = React.useState(1);
  const [previewNumPages, setPreviewNumPages] = React.useState(null);

  const [processingId, setProcessingId] = React.useState(null);
  const previewKind = getPreviewKind(previewContentType, previewFilename);

  const isTextFile = Boolean(String(fileName || '').toLowerCase().match(/\.(txt|md|csv|json|log)$/i));
  const isMediaFile = Boolean(String(fileName || '').toLowerCase().match(/\.(mp4|webm|mov|m4v|ogv|mkv|avi|mp3|wav|m4a|aac|ogg)$/i));

  const handlePreviewClick = () => {
    if (isTextFile || isMediaFile || skipReAuth) {
      // Plain text and media files do not require password re-auth to view
      setAction('preview');
      doPreview(token);
    } else {
      openReAuthFor('preview');
    }
  };

  const openReAuthFor = (which) => {
    setAction(which);
    setShowReAuth(true);
    setReauthPassword('');
    setReauthError('');
  };

  const handleConfirmReAuth = async () => {
    if (!reauthPassword.trim()) {
      setReauthError('Password is required.');
      return;
    }
    if (!decodedToken?.user_email) {
      setReauthError('Invalid authentication context.');
      return;
    }

    setReauthLoading(true);
    setReauthError('');
    try {
      const res = await loginApi({ username: decodedToken.user_email, password: reauthPassword });
      if (res?.is_success) {
        // Since loginApi might now trigger OTP, we just use it to verify the password is correct.
        // We use the existing authenticated token from context for the actual download/preview.
        setShowReAuth(false);
        const useToken = token;
        setAuthToken(useToken);
        if (action === 'preview') await doPreview(useToken);
        if (action === 'download') await doDownload(useToken);
      } else {
        setReauthError('Invalid Authentication.');
      }
    } catch (err) {
      console.error('Reauth error', err);
      setReauthError('Authentication failed.');
    } finally {
      setReauthLoading(false);
    }
  };

  const doPreview = async (token) => {
    if (!fileId) {
      toast.info('No attached file is available to preview.');
      return;
    }
    setProcessingId(fileId);
    setPreviewLoading(true);
    try {
      const res = onPreview ? await onPreview(token, fileId) : await previewProviderFile(token, fileId);
      if (res && res.blob) {
        const url = URL.createObjectURL(res.blob);
        setPreviewUrl(url);
        setPreviewFilename(res.filename || fileName || `file-${fileId}`);
        setPreviewContentType(res.contentType || res.blob.type || '');
        setPreviewNumPages(null);
        setPreviewPage(1);
        setPreviewOpen(true);
      } else {
        toast.error(res?.message || 'Unable to preview file');
      }
    } catch (err) {
      console.error('Preview error', err);
      toast.error(err?.message || 'Unable to preview file');
    } finally {
      setPreviewLoading(false);
      setProcessingId(null);
    }
  };

  const doDownload = async (token) => {
    if (!fileId) {
      toast.info('No downloadable file available for this document.');
      return;
    }
    setProcessingId(fileId);
    try {
      const res = onDownload ? await onDownload(token, fileId) : await downloadProviderFile(token, fileId);
      if (res?.blob) {
        const url = URL.createObjectURL(res.blob);
        const anchor = document.createElement('a');
        anchor.style.display = 'none';
        anchor.href = url;
        anchor.download = res.filename || fileName || `file-${fileId}`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        toast.success('File Downloaded Successfully');
      } else {
        toast.error(res?.message || 'Unable to download file');
      }
    } catch (err) {
      console.error('Download error', err);
      toast.error(err?.message || 'Unable to download file');
    } finally {
      setProcessingId(null);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setPreviewFilename(null);
    setPreviewContentType(null);
    setPreviewNumPages(null);
    setPreviewPage(1);
  };

  return (
    <div className={className}>
      <div className={`d-flex ${compact ? 'gap-1' : 'gap-2'} flex-column ${stack ? '' : 'flex-sm-row'} w-100`}>
        {showPreview && (
          <button
            type="button"
            className={`btn btn-sm btn-warning text-white d-flex align-items-center justify-content-center gap-1 ${compact ? '' : 'px-4'} w-100 flex-grow-1 flex-sm-grow-0`}
            onClick={handlePreviewClick}
            disabled={processingId === fileId}
          >
            {processingId === fileId && action === 'preview' ? (
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
            ) : (
              <MdVisibility size={14} />
            )}
            {!compact && 'Preview'}
          </button>
        )}

        {showDownload && (
          <button
            type="button"
            className={`btn btn-sm hc-btn-next border-0 d-flex align-items-center justify-content-center gap-1 ${compact ? '' : 'px-4'} w-100 flex-grow-1 flex-sm-grow-0`}
            onClick={() => openReAuthFor('download')}
            disabled={processingId === fileId}
          >
            {processingId === fileId && action === 'download' ? (
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
            ) : (
              <MdDownload size={14} />
            )}
            {!compact && 'Download'}
          </button>
        )}
      </div>

      <ReAuthModal
        isOpen={showReAuth}
        description="Enter your password to continue."
        password={reauthPassword}
        onPasswordChange={(v) => { setReauthPassword(v); setReauthError(''); }}
        showPassword={showReauthPassword}
        onTogglePassword={() => setShowReauthPassword((prev) => !prev)}
        error={reauthError}
        loading={reauthLoading}
        attemptsRemaining={null}
        maxAttempts={null}
        onCancel={() => setShowReAuth(false)}
        onConfirm={handleConfirmReAuth}
      />

      {previewOpen && previewKind === 'video' ? (
        <AiVideoPlayerModal
          isOpen={previewOpen}
          onClose={closePreview}
          title={previewFilename || fileName}
          fileName={previewFilename || fileName}
          videoUrl={previewUrl}
          fileId={fileId}
          onDownload={() => {
            setAction('download');
            if (authToken) {
              doDownload(authToken);
            } else {
              openReAuthFor('download');
            }
          }}
        />
      ) : previewOpen && previewKind === 'audio' ? (
        <AiAudioPlayerModal
          isOpen={previewOpen}
          onClose={closePreview}
          title={previewFilename || fileName}
          fileName={previewFilename || fileName}
          audioUrl={previewUrl}
          fileId={fileId}
          onDownload={() => {
            setAction('download');
            if (authToken) {
              doDownload(authToken);
            } else {
              openReAuthFor('download');
            }
          }}
        />
      ) : previewOpen ? (
        <div className="hc-preview-modal position-fixed d-flex align-items-center justify-content-center" style={{ inset: 0, background: 'rgba(var(--hc-black-rgb), 0.6)', zIndex: 2000 }}>
          <div className="mw-100 overflow-hidden position-relative d-flex flex-column" style={{ width: 'min(95%, 1000px)', height: '90%', background: 'var(--hc-surface)', borderRadius: 12 }}>
            <div className="d-flex justify-content-between align-items-center" style={{ padding: 12, borderBottom: '1px solid var(--hc-skeleton-bg)' }}>
              <div>
                <strong>{previewFilename || 'Document Preview'}</strong>
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn hc-btn-next border-0 btn-sm"
                  onClick={() => {
                    setAction('download');
                    if (authToken) {
                      doDownload(authToken);
                    } else {
                      openReAuthFor('download');
                    }
                  }}
                  disabled={!fileId || previewLoading || processingId === fileId}
                >
                  {processingId === fileId ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Downloading
                    </>
                  ) : (
                    <>
                      <MdDownload size={14} />
                      <span className="ms-1">Download</span>
                    </>
                  )}
                </button>
                <button className="btn btn-danger btn-sm text-white" onClick={closePreview}>×</button>
              </div>
            </div>
            <div className="overflow-hidden d-flex flex-column" style={{ flex: 1, padding: 12 }}>
              {previewLoading ? (
                <div className="d-flex align-items-center justify-content-center h-100">Loading preview...</div>
              ) : previewUrl ? (
                previewKind === 'docx' ? (
                  <DocxPreview url={previewUrl} />
                ) : previewKind === 'text' ? (
                  <TextPreview url={previewUrl} filename={previewFilename || fileName} />
                ) : previewKind === 'image' ? (
                  <div className="overflow-auto h-100 d-flex align-items-center justify-content-center">
                    <img src={previewUrl} alt={previewFilename || 'Preview'} className="mw-100 mh-100" style={{ objectFit: 'contain' }} />
                  </div>
                ) : previewKind === 'audio' ? (
                  <div className="d-flex align-items-center justify-content-center h-100 px-3">
                    <audio src={previewUrl} controls className="w-100" style={{ maxWidth: 560 }} />
                  </div>
                ) : previewKind === 'video' ? (
                  <div className="d-flex align-items-center justify-content-center h-100">
                    <video src={previewUrl} controls className="mw-100 mh-100" style={{ borderRadius: 8, background: '#000' }} />
                  </div>
                ) : previewKind === 'pdf' ? (
                  <div className="d-flex flex-column h-100">
                    <div className="overflow-auto d-flex justify-content-center align-items-start" style={{ flex: 1 }}>
                      <div className="mw-100 w-100 d-flex justify-content-center">
                        <Document
                          file={previewUrl}
                          onLoadSuccess={({ numPages }) => { setPreviewNumPages(numPages); setPreviewPage(1); }}
                          onLoadError={(error) => { console.error('PDF load error:', error); toast.error('Unable to render PDF preview'); }}
                        >
                          <Page pageNumber={previewPage} width={Math.min(window.innerWidth * 0.8 - 48, 900)} renderTextLayer={false} renderAnnotationLayer={false} />
                        </Document>
                      </div>
                    </div>
                    {previewNumPages > 1 && (
                      <div className="d-flex align-items-center justify-content-between mt-3">
                        <button type="button" className="btn hc-btn-prev hc-btn-hover-service btn-sm fw-semibold shadow-sm" onClick={() => setPreviewPage((p) => Math.max(p - 1, 1))} disabled={previewPage <= 1}>Previous</button>
                        <span>Page {previewPage} of {previewNumPages}</span>
                        <button type="button" className="btn hc-btn-next border-0 hc-btn-hover-service text-white btn-sm" onClick={() => setPreviewPage((p) => Math.min(p + 1, previewNumPages))} disabled={previewPage >= previewNumPages}>Next</button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center">
                    <p className="mb-1">No preview available for this file type.</p>
                    {previewKind === 'legacy-doc' && (
                      <small className="text-muted">Legacy Word (.doc) files can't be rendered in the browser. Download the file to view it.</small>
                    )}
                  </div>
                )
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100">No preview available</div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
